/**
 * Feb 2026 R293zzg - GstCommentsPopover
 *
 * Inline internal-comments thread for GST documents (quotation /
 * sales order / invoice / credit note). Icon lives in the first
 * column of each list row; opens a popover with the thread and an
 * add box. Comments are workspace-internal (never printed on the
 * PDF, never visible on the client share link).
 */
import { useEffect, useState } from "react";
import { MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";

export default function GstCommentsPopover({ ws, docId, count = 0, onChange }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth() || {};

  useEffect(() => {
    if (!open || !ws || !docId) return;
    setLoading(true);
    api.get(`/gst/workspaces/${ws}/documents/${docId}/comments`)
      .then((r) => setItems(r.data || []))
      .catch(() => { /* silent */ })
      .finally(() => setLoading(false));
  }, [open, ws, docId]);

  const add = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/gst/workspaces/${ws}/documents/${docId}/comments`,
        { body },
      );
      setItems((prev) => [...prev, r.data]);
      setDraft("");
      if (onChange) onChange((items?.length || 0) + 1);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not post comment")); }
    finally { setBusy(false); }
  };

  const remove = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(
        `/gst/workspaces/${ws}/documents/${docId}/comments/${cid}`,
      );
      setItems((prev) => prev.filter((c) => c.id !== cid));
      if (onChange) onChange(Math.max(0, (items?.length || 1) - 1));
    } catch (e) { toast.error(apiErrorMessage(e, "Could not delete comment")); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative inline-flex items-center justify-center w-6 h-6 text-muted-foreground hover:text-primary rounded"
          title={count > 0 ? `${count} internal comment${count === 1 ? "" : "s"}` : "Add an internal comment"}
          data-testid={`gst-comments-btn-${docId}`}
        >
          <MessageSquare className="w-3.5 h-3.5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 text-[8px] bg-primary text-primary-foreground rounded-full min-w-[14px] h-3.5 px-0.5 flex items-center justify-center font-semibold"
                     data-testid={`gst-comments-count-${docId}`}>
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-3 w-96 z-[1300]" data-testid={`gst-comments-content-${docId}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Internal comments
          </div>
          <div className="text-[10px] text-muted-foreground">Not shown on PDF</div>
        </div>
        {loading ? (
          <div className="text-xs text-muted-foreground py-2">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-muted-foreground py-2">No comments yet.</div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {items.map((c) => (
              <div key={c.id} className="text-xs border-l-2 border-border pl-2 group">
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <b className="text-foreground">{c.author_name || "-"}</b>
                  <span>- {fmtRel(c.created_at)}</span>
                  {c.author_id === user?.id && (
                    <button
                      type="button"
                      onClick={() => remove(c.id)}
                      className="ml-auto opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                      title="Delete"
                      data-testid={`gst-comments-delete-${c.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="mt-0.5 whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 pt-2 border-t border-border space-y-1">
          <Textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add an internal comment..."
            className="text-xs rounded-none"
            data-testid={`gst-comments-input-${docId}`}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); add(); }
            }}
          />
          <div className="flex justify-end">
            <Button size="sm" className="h-7 rounded-none text-xs"
                      disabled={busy || !draft.trim()} onClick={add}
                      data-testid={`gst-comments-save-${docId}`}>
              {busy ? "Posting..." : "Post"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function fmtRel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const mins = Math.max(1, Math.floor((Date.now() - d.getTime()) / 60000));
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  } catch { return ""; }
}
